import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Machines from './pages/Machines';
import Customers from './pages/Customers';
import Jobs from './pages/Jobs';
import Tickets from './pages/Tickets';
import Reports from './pages/Reports';
import TicketReports from './pages/TicketReports';
import Users from './pages/Users';
import Schedule from './pages/Schedule';
import SignInPage from './pages/SignIn';
import SignUpPage from './pages/SignUp';
import Profile from './pages/Profile';
import PublicTicketForm from './pages/PublicTicketForm';
import { sharedStyles } from './styles/sharedStyles';
import axios from 'axios';

const ProtectedRoute = ({ children }) => {
  return (
    <>
      <SignedIn>
        {children}
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

const AuthLayout = ({ children }) => {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: sharedStyles.colors.background.default,
      padding: '1rem'
    }}>
      {children}
    </div>
  );
};

const PublicLayout = ({ children }) => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: sharedStyles.colors.background.default,
      padding: '1rem'
    }}>
      {children}
    </div>
  );
};

const MainLayout = ({ children, sidebarOpen, setSidebarOpen }) => {
  const contentStyle = {
    marginLeft: sidebarOpen ? '280px' : '0',
    padding: '2rem',
    minHeight: '100vh',
    backgroundColor: sharedStyles.colors.background.default,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    width: '100%',
    boxSizing: 'border-box',
    transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1)'
  };

  return (
    <div style={{ display: 'flex' }}>
      <SignedIn>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen((open) => !open)} />
      </SignedIn>
      <main className="app-main-print" style={contentStyle}>
        {children}
      </main>
    </div>
  );
};

const AppContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Responsive: hide sidebar by default on small screens
  React.useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    console.log("Backend API URL:", apiUrl);

    axios.get(`${apiUrl}/health`)
      .then(() => {
        console.log("✅ Successfully connected to backend!");
      })
      .catch((err) => {
        console.error("❌ Could not connect to backend:", err.message || err);
      });
  }, []);

  return (
    <Routes>
      {/* Public routes with AuthLayout */}
      <Route path="/sign-in/*" element={
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      } />
      <Route path="/sign-up/*" element={
        <AuthLayout>
          <SignUpPage />
        </AuthLayout>
      } />
      <Route path="/tickets/create" element={
        <PublicLayout>
          <PublicTicketForm />
        </PublicLayout>
      } />
      <Route path="/tickets/public" element={
        <PublicLayout>
          <PublicTicketForm />
        </PublicLayout>
      } />

      {/* Protected routes with MainLayout */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Dashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile/*" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Profile />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/employees" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Employees />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/machines" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Machines />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Customers />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Jobs />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Tickets />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Schedule />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Reports />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/ticket-reports" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <TicketReports />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Users />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
